import { User } from '@/features/user/user.type'

/** Narrow slice of `ProfilePayload` returned on login/register (`UserAuthPayload.profile`). */
export type AuthProfileWire = {
  id: string
  user_id?: string
  display_name?: string
  avatar?: string
  avatar_color?: string
  is_bot?: boolean
  roles?: unknown[]
}

export enum ConfirmationCodeType {
  Login = 'LoginCode',
  Register = 'RegisterCode',
  Security = 'SecurityCode',
  DeleteAccount = 'DeleteAccountCode',
}

export interface ConfirmationCode {
  id: string
  phone_number: string
  expires: string
  type: ConfirmationCodeType
  created: string
}

/** `POST /user/confirm-phone`: the proof `/user/register` requires, and its lifetime in seconds. */
export interface PhoneProof {
  phone_proof: string
  expires_in: number
}

export interface UserAuth {
  user: User
  /** Acting profile for HubHive APIs (`liked_by`, likes, media upload, …). */
  profile?: AuthProfileWire
  role: string
  token: string
  /**
   * Opaque, rotating refresh token (#411, #441, ADR-0042). Present only when the
   * request opted in via `supports_refresh`; exchanged at `/user/auth/refresh`
   * for a fresh access `token`. Absent for the legacy non-expiring token.
   */
  refresh_token?: string
  /** Access-token lifetime in seconds (omitted for the legacy non-expiring token). */
  access_token_expires_in?: number
  id?: string
}

export interface LoginData {
  phone: string
  code: string
}

export interface CountrySelector {
  name: string
  code: string
  dialCode: string
  flag: string
}

/**
 * `POST /user/auth/handoff`'s answer (ADR-0150): a **Handoff code** minted
 * for one Sign-in client, the issuer's redeem endpoint to POST it to, the
 * relative `next` path echoed for the client to post with it, and the code's
 * life in seconds (60). The code is a bearer secret: it is posted once, from
 * this origin, and never placed in a URL, a log or client state.
 */
export interface SignInHandoff {
  code: string
  redeem_url: string
  next: string
  expires_in: number
}

/**
 * `POST /oauth/decide`'s answer (ADR-0150, the Connect page): where the
 * browser goes back to the Sign-in client — its registered redirect URI with
 * the authorization code, or with `error=access_denied` after a refusal.
 */
export interface SignInDecision {
  redirect_to: string
}

/**
 * `GET /oauth/decide?request=…`'s answer (ADR-0150, amended 2026-10-19): the
 * pending request as the Connect page should draw it. `prompt` is what the
 * Sign-in client asked for — `select_account` or `login` when the person is
 * to choose, `''` when the page approves on its own — `client_name` the
 * client's registered name, and `account`, only for a request with a prompt,
 * who the site session is: first name and the phone masked to its last four
 * digits, as the account chooser shows them.
 */
export interface SignInPendingRequest {
  prompt: '' | 'select_account' | 'login'
  client_name: string
  account?: { first_name: string; phone: string }
  /**
   * `'agent'` for a person's own AI agent asking to connect to Loop
   * (ADR-0152): the page draws consent from `consent` instead of deciding on
   * its own. Absent for a first-party Sign-in client.
   */
  kind?: 'agent'
  consent?: AgentConsentDescription
}

/**
 * What the consent screen for a Loop agent connection draws (ADR-0152), as
 * Go describes the pending request: the hosts the agent cannot fake beside
 * its self-asserted name, the scopes offered and which start ticked, and the
 * personal profile the connection will act as.
 */
export interface AgentConsentDescription {
  /** The host that published the agent's client metadata document. */
  client_host?: string
  /** Where the code goes back to. */
  redirect_host: string
  /** The code goes to a program on the person's own computer. */
  loopback_redirect: boolean
  scopes: { scope: string; ticked: boolean }[]
  profile: { name: string; picture?: string }
  /** The person already has a connection for this agent; this updates it. */
  reconnect: boolean
}
